import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { initializeAuth, deleteUser } from '../../react-redux/Slicer/userSlice';
import { restoreUserSession } from '../../react-redux/Slicer/userThunks';
import { isAuthenticated as checkTokenValid } from '../../api/auth';
import { isPublicRoute } from '../../utils/publicRoutes';
import PropTypes from 'prop-types';

/**
 * AuthProvider component that initializes and manages authentication state
 * This should wrap the entire application to ensure auth is checked on mount
 */
const AuthProvider = ({ children }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const isAuthenticated = useSelector(state => state.user?.isAuthenticated);

    useEffect(() => {
        // Initialize auth from localStorage when app loads
        dispatch(initializeAuth());

        // Validate token on mount
        const localToken = localStorage.getItem('token');

        // T2T routes are guarded by their own T2TRouteGuard — skip JWT validation for T2T users
        const isT2TUser = localStorage.getItem('login_type') === 't2t';

        if (!isPublicRoute(location.pathname) && !isT2TUser) {
            // Check if token exists and is valid (OSIF only)
            if (!localToken || !checkTokenValid()) {
                // Token is missing or invalid, clear auth and redirect to login
                dispatch(deleteUser());
                navigate('/', { replace: true, state: { from: location } });
            } else {
                // ── TASK-25May-07 — Hard-refresh user-restore ──
                // Token is valid but `currentUser` (which carries `role`) is
                // not persisted, so it's null after a hard refresh. Without
                // it, every RequireAuth(role=[...]) gate fails its role check
                // and silently bounces to /job-card → blank page. Re-fetch
                // the user profile via the JWT's user_id claim so role-gated
                // routes (Aerial, COO, CDH, etc.) work on direct page loads.
                // Idempotent — the thunk early-returns if currentUser is set.
                dispatch(restoreUserSession());
            }
        }
    }, [dispatch, navigate, location]);

    // Listen for storage events (cross-tab synchronization)
    useEffect(() => {
        const handleStorageChange = (e) => {
            // If token was removed in another tab, logout immediately
            if (e.key === 'token' && !e.newValue) {
                dispatch(deleteUser());
                // Force immediate redirect
                window.location.href = '/';
            }

            // If logout event was triggered in another tab
            if (e.key === 'logout') {
                dispatch(deleteUser());
                // Force immediate redirect
                window.location.href = '/';
            }
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [dispatch]);

    // Continuous token validation - check every time window gains focus
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                const localToken = localStorage.getItem('token');
                const isT2T = localStorage.getItem('login_type') === 't2t';

                if (!isPublicRoute(location.pathname) && !isT2T) {
                    if (!localToken || !checkTokenValid()) {
                        dispatch(deleteUser());
                        window.location.href = '/';
                    }
                }
            }
        };

        const handleFocus = () => {
            const localToken = localStorage.getItem('token');
            const isT2T = localStorage.getItem('login_type') === 't2t';

            if (!isPublicRoute(location.pathname) && !isT2T) {
                if (!localToken || !checkTokenValid()) {
                    dispatch(deleteUser());
                    window.location.href = '/';
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [dispatch, location.pathname]);

    // Periodic token check (every 5 seconds)
    useEffect(() => {
        const intervalId = setInterval(() => {
            const localToken = localStorage.getItem('token');
            const isT2T = localStorage.getItem('login_type') === 't2t';

            if (!isPublicRoute(location.pathname) && isAuthenticated && !isT2T) {
                if (!localToken || !checkTokenValid()) {
                    dispatch(deleteUser());
                    window.location.href = '/';
                }
            }
        }, 5000); // Check every 5 seconds

        return () => clearInterval(intervalId);
    }, [dispatch, location.pathname, isAuthenticated]);

    // ===== BROWSER BACK/FORWARD NAVIGATION BLOCKING - COMMENTED OUT =====
    // Uncomment the code below to prevent browser back/forward button navigation

    // // Prevent browser back/forward navigation for authenticated users
    // useEffect(() => {
    //     if (isAuthenticated) {
    //         const preventNavigation = () => {
    //             window.history.pushState(null, '', window.location.href);
    //         };

    //         preventNavigation();

    //         const handlePopState = (e) => {
    //             e.preventDefault();
    //             preventNavigation();
    //         };

    //         window.addEventListener('popstate', handlePopState);

    //         return () => {
    //             window.removeEventListener('popstate', handlePopState);
    //         };
    //     }
    // }, [isAuthenticated]);

    // ===== END OF BROWSER NAVIGATION BLOCKING CODE =====

    return <>{children}</>;
};

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export default AuthProvider;
